import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Modal, Button, Form, Badge, Spinner, Tabs, Tab } from "react-bootstrap";
import Select from "react-select";
import styled from "@emotion/styled";
import moment from "moment";
import { savOperations } from "state/ducks/store/sav";

/**
 * Notes journalières de suivi d'intendance : historique + ajout.
 * target = { vehicleId, vehicleName, spotId?, spotName? }
 * Sans spotId : notes de la tournée. Avec spotId : notes du point (toutes tournées).
 */
const NotesModal = (props) => {

  const { target, date, onClose } = props;

  const dispatch = useDispatch();

  const [notes, setNotes]         = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving]   = useState(false);
  const [content, setContent]     = useState("");
  // 'tour' = notes de la tournée, 'spots' = notes de tous les points affiliés
  const [activeTab, setActiveTab] = useState("tour");
  // Filtre par point dans l'onglet "spots" (vide = tous)
  const [spotFilter, setSpotFilter] = useState([]);

  // Mode tournée (avec onglets) ou mode point unique
  const isTourMode = !!target && !target.spotId;
  const showSpotsNotes = isTourMode && activeTab === "spots";
  const tourSpots = (target && target.spots) || [];

  const loadNotes = (tab) => {
    if (!target) {
      return;
    }
    setIsLoading(true);

    let params;
    if (target.spotId) {
      // Notes du point limitées au jour de tournée affiché
      params = { spotId: target.spotId, date: date };
    } else if (tab === "spots") {
      // Notes des points affiliés, limitées au jour de tournée affiché
      params = { spotIds: tourSpots.map((spot) => spot.id), date: date };
    } else {
      params = { vehicleId: target.vehicleId };
    }

    dispatch(savOperations.getIntendanceNotes(params))
      .then((result) => {
        setNotes(result?.data?.result || []);
      })
      .catch(() => {
        setNotes([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setContent("");
    setActiveTab("tour");
    setSpotFilter([]);
    loadNotes("tour");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, dispatch]);

  // Notes affichées dans l'onglet "spots" : filtrées par point puis groupées par point
  const filteredSpotIds = spotFilter.map((option) => option.value);
  const visibleNotes = showSpotsNotes && filteredSpotIds.length > 0
    ? notes.filter((note) => filteredSpotIds.includes(note.spot_id))
    : notes;

  const groupedNotes = showSpotsNotes
    ? visibleNotes.reduce((acc, note) => {
        let group = acc.find((item) => item.spotId === note.spot_id);
        if (!group) {
          group = { spotId: note.spot_id, spotName: note.spot_name, notes: [] };
          acc.push(group);
        }
        group.notes.push(note);
        return acc;
      }, [])
    : [];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    loadNotes(tab);
  };

  const handleSave = () => {
    if (!content.trim()) {
      return;
    }
    setIsSaving(true);
    dispatch(savOperations.createIntendanceNote({
      date: date,
      vehicleId: target.vehicleId,
      spotId: target.spotId || null,
      note: content.trim(),
    }))
      .then(() => {
        setContent("");
        loadNotes(activeTab);
      })
      .catch(() => {
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Modal show={!!target} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Notes de suivi — {target && (target.spotName || target.vehicleName)}
          {target && target.spotName &&
            <small className="text-muted d-block" style={{ fontSize: "0.9rem" }}>
              Point de retrait — notes du {moment(date).format("DD/MM/YYYY")}
            </small>
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>

        {isTourMode && (
          <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-3">
            <Tab eventKey="tour" title="Notes de la tournée" />
            <Tab eventKey="spots" title="Notes des points de la tournée" />
          </Tabs>
        )}

        {showSpotsNotes && (
          <>
            <p className="text-muted mb-2">
              Notes du <strong>{moment(date).format("DD/MM/YYYY")}</strong> des points affiliés à cette tournée,
              groupées par point. Pour ajouter une note sur un point, utilisez l'icône{" "}
              <span role="img" aria-label="notes">🗒</span> à côté du point dans la liste.
            </p>
            <Form.Group>
              <Select
                isMulti
                options={tourSpots.map((spot) => ({ value: spot.id, label: spot.name }))}
                value={spotFilter}
                onChange={(options) => setSpotFilter(options || [])}
                placeholder="Filtrer par point de retrait... (vide = tous)"
                noOptionsMessage={() => "Aucun point trouvé"}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
              />
            </Form.Group>
          </>
        )}

        {!showSpotsNotes && (
        <Form.Group>
          <Form.Label>
            Nouvelle note pour le <strong>{moment(date).format("DD/MM/YYYY")}</strong>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Votre note de suivi..."
          />
          <div className="text-right mt-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!content.trim() || isSaving}
              onClick={handleSave}
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-1" />
                  Enregistrement...
                </>
              ) : (
                "Ajouter la note"
              )}
            </Button>
          </div>
        </Form.Group>
        )}

        {!showSpotsNotes && <hr />}

        <h6>Historique</h6>
        {isLoading ? (
          <p>
            <Spinner animation="border" size="sm" className="mr-2" />
            Chargement de l'historique...
          </p>
        ) : (
          <>
            {!showSpotsNotes && notes.length === 0 &&
              <p className="text-muted">
                {target && target.spotId
                  ? `Aucune note pour ce point le ${moment(date).format("DD/MM/YYYY")}.`
                  : "Aucune note pour le moment."}
              </p>
            }
            {showSpotsNotes && visibleNotes.length === 0 &&
              <p className="text-muted">
                Aucune note ce jour-là{filteredSpotIds.length > 0 ? " pour ces points" : ""}.
              </p>
            }

            {/* Onglet points : notes groupées par point */}
            {showSpotsNotes && groupedNotes.map((group) => (
              <div key={group.spotId} className="mb-3">
                <h6 className="mb-2">
                  <Badge variant="info">{group.spotName}</Badge>{" "}
                  <small className="text-muted">{group.notes.length} note(s)</small>
                </h6>
                {group.notes.map((note) => (
                  <NoteCard key={note.id}>
                    <div className="d-flex justify-content-between">
                      <strong>{moment(note.date).format("DD/MM/YYYY")}</strong>
                      <small className="text-muted">
                        {note.author_name && <>par {note.author_name} — </>}
                        saisie le {moment(note.created_at).format("DD/MM/YYYY HH:mm")}
                      </small>
                    </div>
                    {note.vehicle_name &&
                      <Badge variant="secondary" className="mr-1">{note.vehicle_name}</Badge>
                    }
                    <NoteText>{note.note}</NoteText>
                  </NoteCard>
                ))}
              </div>
            ))}

            {/* Mode tournée ou point unique : liste chronologique */}
            {!showSpotsNotes && notes.map((note) => (
              <NoteCard key={note.id}>
                <div className="d-flex justify-content-between">
                  <strong>{moment(note.date).format("DD/MM/YYYY")}</strong>
                  <small className="text-muted">
                    {note.author_name && <>par {note.author_name} — </>}
                    saisie le {moment(note.created_at).format("DD/MM/YYYY HH:mm")}
                  </small>
                </div>
                {target && target.spotId && note.vehicle_name &&
                  <Badge variant="secondary" className="mr-1">{note.vehicle_name}</Badge>
                }
                <NoteText>{note.note}</NoteText>
              </NoteCard>
            ))}
          </>
        )}

      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const NoteCard = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  background: #fdfdfd;
`;

const NoteText = styled.p`
  margin: 0.25rem 0 0 0;
  white-space: pre-wrap;
`;

NotesModal.propTypes = {
  target: PropTypes.shape({
    vehicleId: PropTypes.number,
    vehicleName: PropTypes.string,
    spotId: PropTypes.number,
    spotName: PropTypes.string,
    spots: PropTypes.array, // [{id, name}] points affiliés (mode tournée)
  }),
  date: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

NotesModal.defaultProps = {
  target: null,
};

export default NotesModal;
